import { createEffect, createMemo, createResource, createSignal, For, on, onCleanup, Show } from "solid-js";
import Text from "../../components/Text";
import Modal from "../../components/Modal";
import ItemStackView from "../../components/ItemStackView";
import ItemSlotGrid from "../../components/inventory/ItemSlotGrid";
import { Pagination } from "../../components/Pagination";
import { EmptyState } from "../../components/EmptyState";
import { Skeleton } from "../../components/Skeleton";
import { BulkActionBar } from "../../components/BulkActionBar";
import { showConfirm } from "../../components/Dialog";
import { useToast } from "../../components/MessageBox";
import { useItemName } from '../../hooks/useItemName';
import { useTranslation } from "../../hooks/useTranslation";
import { useTablePref } from "../../hooks/useTablePrefs";
import { useCraftingResolver } from "../../services/craftingResolverContext";
import { fromChestSlot } from "../../services/itemRefAdapters";
import { formatDateTime } from "../../services/dateFormat";
import { STORAGE_ACTIONS } from "../../services/resourceActions";
import {
  useInventoryApi,
  type ChestInfo,
  type ChestQuery,
  type ChestSlotsResponse,
  type FilterItem,
  type StorageInfo,
} from "../../services/inventoryApi";
import SetupModeButton from "../../components/SetupModeButton";
import SellChamberNote from "../../components/inventory/SellChamberNote";
import type { SetupModeValueDto } from "../../api/generated/schemas";
import ChestConfigModal, { UNCHANGED, type ChestConfigValue } from "./ChestConfigModal";
import shared from "./storageShared.module.css";
import styles from "./StorageChestsTab.module.css";

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE = 50;

// ChestSlot.InfiniteBackingSlotIndex: the hidden stock an infinite chest holds behind its visible
// slots. It arrives in the same list, so it has to be pulled out before the grid renders.
const INFINITE_BACKING_SLOT = -1;

type SortKey = "coords" | "type" | "scanned";

interface Props {
  storage: StorageInfo;
  assignedBotIds: string[];
  onChestsChanged: () => Promise<void>;
}

function typeBadgeClass(type: number) {
  if (type === 2) return styles.badgeInfinite;
  if (type === 1) return styles.badgeDouble;
  return styles.badgeSingle;
}

function typeBadgeLabel(type: number) {
  if (type === 2) return "STORAGE_CHEST_BADGE_INFINITE";
  if (type === 1) return "STORAGE_CHEST_BADGE_DOUBLE";
  return "STORAGE_CHEST_BADGE_SINGLE";
}

export default function StorageChestsTab(props: Props) {
  const { t } = useTranslation();
  const toast = useToast();
  const inv = useInventoryApi();
  const resolver = useCraftingResolver();
  const { itemName } = useItemName();

  const [search, setSearch] = createSignal("");
  const [debouncedSearch, setDebouncedSearch] = createSignal("");
  const [typeFilter, setTypeFilter] = useTablePref<number | null>("storageChests.type", null);
  const [onlyNever, setOnlyNever] = useTablePref<boolean>("storageChests.neverScanned", false);
  const [onlyFiltered, setOnlyFiltered] = useTablePref<boolean>("storageChests.filtered", false);
  const [sortKey, setSortKey] = useTablePref<SortKey>("storageChests.sort", "coords");
  const [sortDesc, setSortDesc] = useTablePref<boolean>("storageChests.sortDesc", false);
  const [page, setPage] = createSignal(0);

  const [selected, setSelected] = createSignal<Set<string>>(new Set());
  const [allMatchingSelected, setAllMatchingSelected] = createSignal(false);

  const [configChest, setConfigChest] = createSignal<ChestInfo | null>(null);
  const [configOpen, setConfigOpen] = createSignal(false);
  const [configBulkCount, setConfigBulkCount] = createSignal<number | null>(null);
  const [configSaving, setConfigSaving] = createSignal(false);

  const [slotsChest, setSlotsChest] = createSignal<ChestInfo | null>(null);
  const [chestSlots, setChestSlots] = createSignal<ChestSlotsResponse | null>(null);
  const [slotsLoading, setSlotsLoading] = createSignal(false);

  const mayWrite = () => props.storage.myActions.includes(STORAGE_ACTIONS.ChestsWrite);
  const mayRunTasks = () => props.storage.myActions.includes(STORAGE_ACTIONS.TasksRun);

  createEffect(on(search, (value) => {
    const timer = setTimeout(() => { setDebouncedSearch(value); setPage(0); }, SEARCH_DEBOUNCE_MS);
    onCleanup(() => clearTimeout(timer));
  }, { defer: true }));

  const filterQuery = (): ChestQuery => ({
    search: debouncedSearch().trim(),
    type: typeFilter(),
    filterMode: onlyFiltered() ? 1 : null,
    neverScanned: onlyNever(),
  });

  // Equality on the values, not the object: a mutation replaces the parent's storage object, and
  // without this the resource refetches a second time for a query that did not change.
  const chestQuery = createMemo(
    () => ({
      storageId: props.storage.id,
      search: debouncedSearch().trim(),
      type: typeFilter(),
      onlyFiltered: onlyFiltered(),
      neverScanned: onlyNever(),
      sort: sortKey(),
      desc: sortDesc(),
      page: page(),
    }),
    undefined,
    { equals: (a, b) => JSON.stringify(a) === JSON.stringify(b) },
  );

  const [chestPage, { refetch }] = createResource(
    chestQuery,
    async (params) => inv.fetchChests(params.storageId, {
      search: params.search,
      type: params.type,
      filterMode: params.onlyFiltered ? 1 : null,
      neverScanned: params.neverScanned,
      sort: params.sort,
      desc: params.desc,
      page: params.page,
      pageSize: PAGE_SIZE,
    }),
  );

  const chests = () => chestPage()?.chests ?? [];
  const total = () => chestPage()?.total ?? 0;
  const totalPages = createMemo(() => Math.ceil(total() / PAGE_SIZE));
  const selectedCount = () => allMatchingSelected() ? total() : selected().size;

  function clearSelection() {
    setSelected(new Set<string>());
    setAllMatchingSelected(false);
  }

  function toggleRow(chestId: string) {
    setAllMatchingSelected(false);
    setSelected(previous => {
      const next = new Set<string>(previous);
      if (next.has(chestId)) next.delete(chestId);
      else next.add(chestId);
      return next;
    });
  }

  function togglePage() {
    const pageIds = chests().map(c => c.id);
    const allOnPage = pageIds.length > 0 && pageIds.every(id => selected().has(id));
    setAllMatchingSelected(false);
    setSelected(previous => {
      const next = new Set<string>(previous);
      for (const id of pageIds) {
        if (allOnPage) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  }

  function toggleSort(key: SortKey) {
    if (sortKey() === key) setSortDesc(d => !d);
    else { setSortKey(key); setSortDesc(false); }
    setPage(0);
  }

  function sortIcon(key: SortKey) {
    if (sortKey() !== key) return "fa-sort";
    return sortDesc() ? "fa-sort-down" : "fa-sort-up";
  }

  function resetFilters() {
    setTypeFilter(null);
    setOnlyNever(false);
    setOnlyFiltered(false);
    setPage(0);
  }

  function setPill<T>(setter: (value: T) => void, value: T) {
    setter(value);
    setPage(0);
  }

  function filterItemLabel(item: FilterItem) {
    const r = resolver();
    const def = r?.getItemById(item.itemId, item.metadata) ?? r?.getItemById(item.itemId);
    return def ? itemName(def.key) : `${item.itemId}:${item.metadata}`;
  }

  function filterSummary(chest: ChestInfo) {
    const items = chest.filterItems ?? [];
    if (items.length === 1) return filterItemLabel(items[0]);
    return t("STORAGE_FILTER_N_ITEMS", items.length);
  }

  // Every mutation lands here: refresh the page in place and let the shell refresh the rail's count.
  async function afterMutation() {
    clearSelection();
    await refetch();
    await props.onChestsChanged();
  }

  // The capture edits the whole storage, not the page in view, so it starts from every chest the
  // storage has: clicking one of those in game removes it, clicking a fresh chest adds it.
  async function chestSetValue(): Promise<SetupModeValueDto> {
    const all = await inv.fetchAllChests(props.storage.id);
    return {
      kind: "ChestSet",
      chests: all.map((chest) => ({
        x: chest.x, y: chest.y, z: chest.z,
        x2: chest.x2 ?? null, y2: chest.y2 ?? null, z2: chest.z2 ?? null,
        type: chest.typeName,
      })),
    };
  }

  async function applyChestSet(value: SetupModeValueDto) {
    const picked = value.chests ?? [];
    const before = await inv.fetchAllChests(props.storage.id);
    const key = (x: number, y: number, z: number) => `${x}:${y}:${z}`;
    const keptKeys = new Set(picked.map((chest) => key(chest.x, chest.y, chest.z)));

    const removed = before.filter((chest) => !keptKeys.has(key(chest.x, chest.y, chest.z)));
    const beforeKeys = new Set(before.map((chest) => key(chest.x, chest.y, chest.z)));
    const added = picked.filter((chest) => !beforeKeys.has(key(chest.x, chest.y, chest.z)));

    if (added.length === 0 && removed.length === 0) {
      toast?.info(t("STORAGE_PICK_NO_CHANGE"));
      return;
    }

    try {
      for (const chest of added) await inv.addChest(props.storage.id, chest.x, chest.y, chest.z);
      if (removed.length > 0) {
        await inv.deleteChests(props.storage.id, { chestIds: removed.map((chest) => chest.id) });
      }
      toast?.success(t("STORAGE_PICK_APPLIED", added.length, removed.length));
      await afterMutation();
    } catch (err) {
      toast?.error(err instanceof Error ? err.message : t("STORAGE_PICK_FAILED"));
    }
  }

  function openSingleConfig(chest: ChestInfo) {
    setConfigChest(chest);
    setConfigBulkCount(null);
    setConfigOpen(true);
  }

  function openBulkConfig() {
    setConfigChest(null);
    setConfigBulkCount(selectedCount());
    setConfigOpen(true);
  }

  async function saveConfig(value: ChestConfigValue) {
    setConfigSaving(true);
    try {
      const chest = configChest();
      if (chest) {
        await inv.updateChestConfig(props.storage.id, chest.id, {
          type: value.type,
          filterMode: value.filterMode,
          filterItems: value.filterItems,
        });
      } else {
        const updated = await inv.bulkUpdateChests(props.storage.id, {
          ...selectionBody(),
          type: value.type === UNCHANGED ? null : value.type,
          filterMode: value.filterMode === UNCHANGED ? null : value.filterMode,
          filterItems: value.filterMode === UNCHANGED ? undefined : value.filterItems,
        });
        toast?.success(t("STORAGE_BULK_UPDATED", updated));
      }
      setConfigOpen(false);
      await afterMutation();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_SAVE_FAILED"));
    } finally {
      setConfigSaving(false);
    }
  }

  // "Select all matching" must not ship hundreds of ids, so it sends the filter the user is looking
  // at and lets the backend resolve the same set.
  function selectionBody() {
    return allMatchingSelected()
      ? { match: filterQuery() }
      : { chestIds: [...selected()] };
  }

  async function deleteChest(chest: ChestInfo) {
    if (!await showConfirm(t("STORAGE_DELETE_CHEST_CONFIRM", chest.x, chest.y, chest.z), true)) return;
    try {
      await inv.deleteChests(props.storage.id, { chestIds: [chest.id] });
      await afterMutation();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_DELETE_CHEST_FAILED", e?.message ?? ""));
    }
  }

  async function deleteSelected() {
    if (!await showConfirm(t("STORAGE_BULK_DELETE_CONFIRM", selectedCount()), true)) return;
    try {
      const deleted = await inv.deleteChests(props.storage.id, selectionBody());
      toast?.success(t("STORAGE_BULK_DELETED", deleted));
      await afterMutation();
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_DELETE_FAILED"));
    }
  }

  async function rescan(chestIds: string[]) {
    try {
      await inv.readChests(props.storage.id, chestIds);
      toast?.success(chestIds.length === 1
        ? t("STORAGE_RESCAN_QUEUED_ONE")
        : t("STORAGE_RESCAN_QUEUED", chestIds.length));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_UNKNOWN"));
    }
  }

  async function openSlots(chest: ChestInfo) {
    setSlotsChest(chest);
    setChestSlots(null);
    setSlotsLoading(true);
    try {
      setChestSlots(await inv.getChestSlots(props.storage.id, chest.id));
    } catch (e: any) {
      toast?.error(e?.message ?? t("STORAGE_SLOTS_LOAD_FAILED"));
      setSlotsChest(null);
    } finally {
      setSlotsLoading(false);
    }
  }

  const backingSlot = () => chestSlots()?.slots.find(s => s.slotIndex === INFINITE_BACKING_SLOT) ?? null;
  const visibleSlots = () => chestSlots()?.slots.filter(s => s.slotIndex !== INFINITE_BACKING_SLOT) ?? [];

  const hasFilters = () => typeFilter() !== null || onlyNever() || onlyFiltered() || debouncedSearch().trim() !== "";

  const PickChestsButton = () => (
    <Show when={mayWrite() && !props.storage.sellChamberBotId}>
      <SetupModeButton
        fieldId={`storage.${props.storage.id}.chests`}
        kind="ChestSet"
        label={t("STORAGE_PICK_CHESTS")}
        hint={props.storage.name}
        citybuild={props.storage.citybuild}
        allowedBots={props.assignedBotIds}
        currentValue={chestSetValue}
        onCaptured={(value) => void applyChestSet(value)}
      />
    </Show>
  );

  return (
    <>
      <Show when={props.storage.sellChamberBotId}>
        <SellChamberNote storage={props.storage} />
      </Show>
      <div class={shared.toolbar}>
        <div class={styles.search}>
          <i class="fa-solid fa-magnifying-glass" />
          <input value={search()} onInput={e => setSearch(e.currentTarget.value)} placeholder={t("STORAGE_SEARCH_CHESTS")} />
        </div>
        <div class={shared.pills}>
          <button class={`${shared.pill} ${!hasFilters() ? shared.pillOn : ""}`} onClick={resetFilters}>
            <Text val="STORAGE_FILTER_ALL" />
          </button>
          <button
            class={`${shared.pill} ${typeFilter() === 0 ? shared.pillOn : ""}`}
            onClick={() => setPill(setTypeFilter, typeFilter() === 0 ? null : 0)}
          >
            <Text val="STORAGE_STAT_SINGLE" />
          </button>
          <button
            class={`${shared.pill} ${typeFilter() === 1 ? shared.pillOn : ""}`}
            onClick={() => setPill(setTypeFilter, typeFilter() === 1 ? null : 1)}
          >
            <Text val="STORAGE_STAT_DOUBLE" />
          </button>
          <button
            class={`${shared.pill} ${typeFilter() === 2 ? shared.pillOn : ""}`}
            onClick={() => setPill(setTypeFilter, typeFilter() === 2 ? null : 2)}
          >
            <Text val="STORAGE_STAT_INFINITE" />
          </button>
          <button
            class={`${shared.pill} ${onlyNever() ? shared.pillOn : ""}`}
            onClick={() => setPill(setOnlyNever, !onlyNever())}
          >
            <Text val="STORAGE_FILTER_NEVER_SCANNED" />
          </button>
          <button
            class={`${shared.pill} ${onlyFiltered() ? shared.pillOn : ""}`}
            onClick={() => setPill(setOnlyFiltered, !onlyFiltered())}
          >
            <Text val="STORAGE_FILTER_HAS_FILTER" />
          </button>
        </div>
      </div>

      <Show when={chestPage.state !== "pending"} fallback={<Skeleton variant="rect" height="20rem" />}>
        <Show when={total() > 0} fallback={
          <Show when={hasFilters()} fallback={
            <EmptyState
              illustration="inventory"
              title={t("STORAGE_NO_CHESTS")}
              hint={t("STORAGE_NO_CHESTS_HINT")}
              action={<PickChestsButton />}
            />
          }>
            <EmptyState
              icon="fa-filter-circle-xmark"
              title={t("STORAGE_NO_CHESTS_MATCH")}
              action={<button class="btn btn-secondary btn-sm" onClick={resetFilters}><Text val="STORAGE_FILTER_ALL" /></button>}
            />
          </Show>
        }>
          <div class={shared.showing}>
            <span>{t("STORAGE_SHOWING", chests().length, total())}</span>
            <Show when={props.storage.scannedChestCount < props.storage.chestCount}>
              <span>{t("STORAGE_UNSCANNED_HINT", props.storage.chestCount - props.storage.scannedChestCount)}</span>
            </Show>
            <PickChestsButton />
          </div>

          <div class={shared.tableWrap}>
            <table class={shared.table}>
              <thead>
                <tr>
                  <Show when={mayWrite()}>
                    <th class={shared.thSelect}>
                      <input
                        type="checkbox"
                        aria-label={t("SELECTALL")}
                        checked={chests().length > 0 && chests().every(c => selected().has(c.id))}
                        onChange={togglePage}
                      />
                    </th>
                  </Show>
                  <th class={shared.thSort} onClick={() => toggleSort("type")}>
                    <Text val="STORAGE_TYPE" /> <i class={`fa-solid ${sortIcon("type")}`} />
                  </th>
                  <th class={shared.thSort} onClick={() => toggleSort("coords")}>
                    <Text val="STORAGE_COL_COORDS" /> <i class={`fa-solid ${sortIcon("coords")}`} />
                  </th>
                  <th class={shared.thSort} onClick={() => toggleSort("scanned")}>
                    <Text val="STORAGE_COL_SCANNED" /> <i class={`fa-solid ${sortIcon("scanned")}`} />
                  </th>
                  <th><Text val="STORAGE_FILTER" /></th>
                  <th class={shared.thRight}><Text val="STORAGE_COL_ACTIONS" /></th>
                </tr>
              </thead>
              <tbody>
                <For each={chests()}>
                  {(chest) => (
                    <tr class={selected().has(chest.id) || allMatchingSelected() ? shared.rowSelected : ""}>
                      <Show when={mayWrite()}>
                        <td>
                          <input
                            type="checkbox"
                            aria-label={`${chest.x} ${chest.y} ${chest.z}`}
                            checked={selected().has(chest.id) || allMatchingSelected()}
                            onChange={() => toggleRow(chest.id)}
                          />
                        </td>
                      </Show>
                      <td>
                        <span class={`${styles.typeBadge} ${typeBadgeClass(chest.type)}`}>
                          {t(typeBadgeLabel(chest.type))}
                        </span>
                      </td>
                      <td class={styles.coords}>
                        <span class={styles.ax}>x</span>{chest.x} <span class={styles.ax}>y</span>{chest.y} <span class={styles.ax}>z</span>{chest.z}
                      </td>
                      <td>
                        <Show when={chest.lastScannedAt} fallback={<span class={styles.never}><Text val="STORAGE_NEVER_SCANNED" /></span>}>
                          <span class={styles.scanned}>{formatDateTime(chest.lastScannedAt!)}</span>
                        </Show>
                      </td>
                      <td>
                        <Show when={chest.filterMode !== 0} fallback={<span class={styles.filterNone}>-</span>}>
                          <span class={styles.filterCell} title={chest.filterModeName}>
                            <i class="fa-solid fa-filter" /> <span class={styles.filterName}>{filterSummary(chest)}</span>
                          </span>
                        </Show>
                      </td>
                      <td>
                        <div class={shared.rowActions}>
                          <Show when={mayWrite()}>
                            <button class={shared.iconBtn} title={t("STORAGE_CONFIGURE")} onClick={() => openSingleConfig(chest)}>
                              <i class="fa-solid fa-gear" />
                            </button>
                          </Show>
                          <button
                            class={shared.iconBtn}
                            title={t("STORAGE_VIEW_SLOTS")}
                            onClick={() => void openSlots(chest)}
                            disabled={!chest.lastScannedAt}
                          >
                            <i class="fa-solid fa-box-open" />
                          </button>
                          <Show when={mayRunTasks()}>
                            <button class={shared.iconBtn} title={t("STORAGE_RESCAN_CHEST")} onClick={() => void rescan([chest.id])}>
                              <i class="fa-solid fa-rotate" />
                            </button>
                          </Show>
                          <Show when={mayWrite()}>
                            <button
                              class={`${shared.iconBtn} ${shared.iconBtnDanger}`}
                              title={t("STORAGE_DELETE_CHEST")}
                              onClick={() => void deleteChest(chest)}
                            >
                              <i class="fa-solid fa-trash" />
                            </button>
                          </Show>
                        </div>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>

          <div class={shared.pager}>
            <Pagination page={page()} totalPages={totalPages()} onChange={setPage} size="sm" />
          </div>

          <BulkActionBar
            selectedCount={selectedCount()}
            matchingCount={total()}
            allMatchingSelected={allMatchingSelected()}
            onSelectAllMatching={() => setAllMatchingSelected(true)}
            onClear={clearSelection}
            actions={
              <>
                <button class="btn btn-secondary btn-sm" onClick={openBulkConfig}>
                  <i class="fa-solid fa-gear" /> <Text val="STORAGE_CONFIGURE" />
                </button>
                <Show when={mayRunTasks() && !allMatchingSelected()}>
                  <button class="btn btn-secondary btn-sm" onClick={() => void rescan([...selected()])}>
                    <i class="fa-solid fa-rotate" /> <Text val="STORAGE_RESCAN_SELECTED" />
                  </button>
                </Show>
                <button class="btn btn-danger btn-sm" onClick={() => void deleteSelected()}>
                  <i class="fa-solid fa-trash" /> <Text val="DELETE" />
                </button>
              </>
            }
          />
        </Show>
      </Show>

      <ChestConfigModal
        open={configOpen()}
        chest={configChest()}
        bulkCount={configBulkCount()}
        saving={configSaving()}
        onClose={() => setConfigOpen(false)}
        onSave={(value) => void saveConfig(value)}
      />

      <Modal
        open={!!slotsChest()}
        onClose={() => { setSlotsChest(null); setChestSlots(null); }}
        title={<Text val="STORAGE_CHEST_SLOTS" />}
        leading={<i class="fa-solid fa-box-open" />}
        maxWidth="640px"
      >
        <Show when={slotsLoading()}>
          <div class={styles.modalLoading}><i class="fa-solid fa-spinner fa-spin fa-2x" /></div>
        </Show>
        <Show when={!slotsLoading() && chestSlots()}>
          <Show when={backingSlot()}>
            {(slot) => (
              <div class={styles.backingSlot}>
                <ItemStackView stack={fromChestSlot(slot())} size={40} bare showTooltip />
                <div class={styles.backingSlotText}>
                  <span>{t("STORAGE_INFINITE_BACKING", slot().count)}</span>
                  <span class={styles.backingSlotHint}><Text val="STORAGE_INFINITE_BACKING_HINT" /></span>
                </div>
              </div>
            )}
          </Show>
          <Show when={visibleSlots().length > 0} fallback={
            <Show when={!backingSlot()}>
              <EmptyState illustration="inventory" title={t("STORAGE_NO_ITEMS_IN_CHEST")} />
            </Show>
          }>
            <ItemSlotGrid
              compact
              items={visibleSlots().map(slot => ({
                key: String(slot.slotIndex),
                stack: fromChestSlot(slot),
              }))}
            />
          </Show>
        </Show>
      </Modal>
    </>
  );
}
